import React from 'react';
import { Button, SystemPage } from './kit';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary Component
 *
 * -- Migrated onto the kit, Phase 6 ----------------------------------------
 *
 * Kit 07 item 162, the server-error page, and it was the LAST consumer of
 * `components/ui/*` anywhere in the build.
 *
 * It matters more than its size: this is the fallback that renders when any
 * route throws, so it is on every route by construction, and it was drawn in
 * eleven quarantined variables and a raw `12px`. Item 162's keyboard note is
 * "Try again is the first stop", which it now is because it is first in the
 * DOM.
 *
 * The dev-only stack trace stays. It is behind `import.meta.env.DEV`, it is
 * the only thing on the page a developer actually wants, and item 162's
 * `reference` slot is for a support code rather than a stack.
 * 
 * Catches JavaScript errors anywhere in the child component tree
 * and displays a fallback UI instead of crashing the entire app.
 * 
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console (in production, send to error tracking service)
    console.error('Error Boundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="upm-system-page" data-kit>
          <SystemPage
            kind="error"
            title="Something went wrong"
            description="We've encountered an unexpected error. Please try refreshing the page."
            action={
              <div className="upm-row">
                <Button onClick={this.handleReset}>Try Again</Button>
                <Button variant="secondary" onClick={() => { window.location.href = "/"; }}>
                  Go Home
                </Button>
              </div>
            }
          />

          {/* Dev only: the stack, which is the one thing a developer wants and
              nothing a user should see. */}
          {import.meta.env.DEV && this.state.error && (
            <details className="upm-system-page__trace">
              <summary>Error details (dev mode)</summary>
              <pre>
                {this.state.error.toString()}
                {this.state.error.stack && `\n\n${this.state.error.stack}`}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Convenience hook for throwing errors that will be caught by ErrorBoundary
 * 
 * Usage:
 * const throwError = useErrorHandler();
 * 
 * try {
 *   // something that might fail
 * } catch (error) {
 *   throwError(error);
 * }
 */
export function useErrorHandler() {
  const [, setError] = React.useState();
  
  return React.useCallback((error: Error) => {
    setError(() => {
      throw error;
    });
  }, []);
}
